import { EmailDomain } from "@/types/EmailDomain";
import { apiDelete, apiPut, apiGet, apiPost } from "../_apiShim";

const BASE_PATH = "/api/v1/email-domains";

// Create an email domain
export async function createEmailDomain(
  domain: string,
  company: boolean,
  companyId?: string,
  eventId?: string
): Promise<EmailDomain> {
  return await apiPost<EmailDomain>(
    BASE_PATH,
    {
      eventId,
      companyId,
      domain,
      company,
    },
    {
      "Cache-Control": "no-store",
    },
    true
  );
}

// Get email domain by ID
export async function getEmailDomainById(
  emailDomainId: string,
  fields: string = "emailDomainId,eventId,companyId,domain,dnsAuthValidation,dnsEntryDKIM1,dnsEntryDKIM2,dnsEntryDKIM3,dnsCustom,dnsStateAuthValidation,dnsStateDKIM,dnsStateCustom,emailDomainState,ownershipProofRequired,identityType,localPart,dmarcPolicy,failureReason,sesVerifyVia,sesLinkSentAt,proofVerifiedAt,otpRequestedBy,otpRequestedAt,dkimCovered,sesCheckedAt,sesCheckOutcome"
): Promise<EmailDomain> {
  return await apiGet<EmailDomain>(
    `${BASE_PATH}/${emailDomainId}?fields=${encodeURIComponent(fields)}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );
}

// Update an email domain
export async function updateEmailDomain(
  emailDomainId: string,
  eventId?: string,
  companyId?: string,
  domain?: string,
  dnsAuthValidation?: string,
  dnsEntryDKIM1?: string,
  dnsEntryDKIM2?: string,
  dnsEntryDKIM3?: string,
  dnsCustom?: string,
  dnsStateAuthValidation?: string,
  dnsStateDKIM?: string,
  dnsStateCustom?: string,
  emailDomainState?: string
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}/${emailDomainId}`,
    {
      eventId,
      companyId,
      domain,
      dnsAuthValidation,
      dnsEntryDKIM1,
      dnsEntryDKIM2,
      dnsEntryDKIM3,
      dnsCustom,
      dnsStateAuthValidation,
      dnsStateDKIM,
      dnsStateCustom,
      emailDomainState,
    },
    {
      "Cache-Control": "no-store",
    },
  );
}

// Delete an email domain (requires either eventId or companyId)
export async function deleteEmailDomainInEvent(
  emailDomainId: string,
  eventId: string
): Promise<boolean> {
  return await apiDelete<boolean>(
    `${BASE_PATH}/${emailDomainId}?eventId=${eventId}`,
    {
      "Cache-Control": "no-store",
    }
  );
}

// Delete an email domain by company
export async function deleteEmailDomainInCompany(
  emailDomainId: string,
  companyId: string
): Promise<boolean> {
  return await apiDelete<boolean>(
    `${BASE_PATH}/${emailDomainId}?companyId=${companyId}`,
    {
      "Cache-Control": "no-store",
    }
  );
}

// Get email domains by event ID with pagination. The event's rows PLUS the
// owning company's — resolved server-side from the event. No companyId: the
// backend ignores the query param and the cookie company is the wrong tenant
// on another company's event (event-scope wave).
export async function getEmailDomainsByEvent(
  eventId: string,
  page: number = 0,
  limit: number = 10,
  fields: string = "emailDomainId,eventId,companyId,domain,dnsAuthValidation,dnsEntryDKIM1,dnsEntryDKIM2,dnsEntryDKIM3,dnsCustom,dnsStateAuthValidation,dnsStateDKIM,dnsStateCustom,emailDomainState,ownershipProofRequired,identityType,localPart,dmarcPolicy,failureReason,sesVerifyVia,sesLinkSentAt,proofVerifiedAt,otpRequestedAt,dkimCovered,sesCheckedAt,sesCheckOutcome"
): Promise<EmailDomain[]> {
  return await apiGet<EmailDomain[]>(
    `${BASE_PATH}/event/${eventId}?page=${page}&limit=${limit}&fields=${encodeURIComponent(
      fields
    )}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );
}

// Get email domains by company ID with pagination
export async function getEmailDomainsByCompany(
  companyId: string,
  page: number = 0,
  limit: number = 10,
  fields: string = "emailDomainId,eventId,companyId,domain,dnsAuthValidation,dnsEntryDKIM1,dnsEntryDKIM2,dnsEntryDKIM3,dnsCustom,dnsStateAuthValidation,dnsStateDKIM,dnsStateCustom,emailDomainState,ownershipProofRequired,identityType,localPart,dmarcPolicy,failureReason,sesVerifyVia,sesLinkSentAt,proofVerifiedAt,otpRequestedAt,dkimCovered,sesCheckedAt,sesCheckOutcome"
): Promise<EmailDomain[]> {
  return await apiGet<EmailDomain[]>(
    `${BASE_PATH}/company/${companyId}?page=${page}&limit=${limit}&fields=${encodeURIComponent(
      fields
    )}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );
}

// ---------------------------------------------------------------------------
// Single-address sender identities (`identityType: 'ADDRESS'`).
// ---------------------------------------------------------------------------

/** 200 body of `POST /email-domains/{id}/confirm-otp`. */
export type ConfirmSenderAddressOtpResult = {
  emailDomain: EmailDomain;
  /** The one-time code was accepted (or had already been accepted). */
  proofOk: boolean;
  /** The AWS confirmation link went out in this request. */
  linkSent: boolean;
  /**
   * Set when the proof succeeded but the next step did not:
   * `senderDomainDmarcReject | workerUnavailable | verificationMailThrottled`.
   */
  errorCode?: string | null;
};

/** 200 body of `POST /email-domains/{id}/resend-verification`. */
export type ResendSenderAddressVerificationResult = {
  emailDomain: EmailDomain;
  /** `otp` = the code step was re-armed; `link` = the AWS link step. */
  kind: "otp" | "link";
  /**
   * A mail actually went out in this request. False when the backend
   * declined to mail (e.g. throttled) — `errorCode` then says why.
   */
  mailSent: boolean;
  /** Why no mail went out: `verificationMailThrottled | workerUnavailable | …`. */
  errorCode?: string | null;
};

// Register a single address as a sender identity (mails a one-time code).
export async function createSenderAddressIdentity(
  address: string,
  company: boolean,
  eventId?: string
): Promise<EmailDomain> {
  return await apiPost<EmailDomain>(
    `${BASE_PATH}/address`,
    { eventId, company, address },
    { "Cache-Control": "no-store" },
    true
  );
}

// Confirm the mailed one-time code for an ADDRESS row.
export async function confirmSenderAddressOtp(
  emailDomainId: string,
  code: string
): Promise<ConfirmSenderAddressOtpResult> {
  return await apiPost<ConfirmSenderAddressOtpResult>(
    `${BASE_PATH}/${emailDomainId}/confirm-otp`,
    { code },
    { "Cache-Control": "no-store" },
    true
  );
}

// Re-send the code (before the proof) or the AWS link (after it).
export async function resendSenderAddressVerification(
  emailDomainId: string
): Promise<ResendSenderAddressVerificationResult> {
  return await apiPost<ResendSenderAddressVerificationResult>(
    `${BASE_PATH}/${emailDomainId}/resend-verification`,
    {},
    { "Cache-Control": "no-store" },
    true
  );
}
